import { z } from "zod";

import { enumReadSchema } from "../../../../shared/core/enum-read.schema";

// ----------------------------------------------------------------------------
// Enumerations — hand-rolled mirrors of the Prisma enums
// ----------------------------------------------------------------------------

/**
 * Mirrors the Prisma enum `VibeAppEnvEntryKind`. Hand-rolled (rather than
 * `z.nativeEnum($Enums.VibeAppEnvEntryKind)`) so `@nexus/types` does not pull
 * Prisma's runtime into the frontend bundle — the same arrangement
 * `VibeEnvVarScopeSchema` already has.
 */
export const VIBE_APP_ENV_ENTRY_KINDS = ["LITERAL", "VAULT_SECRET", "CARD"] as const;
export const VibeAppEnvEntryKindSchema = z.enum(VIBE_APP_ENV_ENTRY_KINDS);
export type VibeAppEnvEntryKindValue = z.infer<typeof VibeAppEnvEntryKindSchema>;

/**
 * How an imported access card is DELIVERED into the app's environment.
 * Mirrors the Prisma enum `VibeCardProjection`.
 *
 * All four members are carried on the READ shape so a row written by a newer
 * backend renders as a known name rather than an unrecognised string. Only
 * `HANDLE` is selectable on the write path — see
 * {@link ImportableVibeCardProjectionSchema}.
 */
export const VIBE_CARD_PROJECTIONS = ["HANDLE", "SENTINEL", "AMBIENT", "LEASED_TOKEN"] as const;
export const VibeCardProjectionSchema = z.enum(VIBE_CARD_PROJECTIONS);

/** `VibeCardProjectionSchema` as a reader parses it: a value this build does not list reads as unlisted. */
export const VibeCardProjectionReadSchema = enumReadSchema(VIBE_CARD_PROJECTIONS);
export type VibeCardProjectionValue = z.infer<typeof VibeCardProjectionSchema>;

/**
 * The projections a card may actually be imported UNDER today.
 *
 * A literal rather than a narrowed enum, and it is the refusal itself rather
 * than a note about one: the other three delivery paths put credential
 * material at or near the app and each ships behind its own enforcement. A
 * request naming one is refused at the Zod boundary, before any grant exists
 * to be cleaned up.
 */
export const ImportableVibeCardProjectionSchema = z.literal("HANDLE");

/**
 * Whether the app may use the card RIGHT NOW.
 *
 * DERIVED server-side from the grant's status and the card's own lifecycle
 * columns, never stored. Deriving it once, on the server, is what stops the
 * console, the CLI and the deployer reaching three different answers from the
 * same three timestamps.
 *
 * Only `ACTIVE` projects into a deployment. Every other value means the next
 * deploy refuses this entry and names it, which is why the status travels as a
 * first-class field and not as a detail behind an expansion.
 */
export const VIBE_CARD_BINDING_STATUSES = [
  "ACTIVE",
  "PENDING_APPROVAL",
  "PAUSED",
  "REVOKED",
  "EXPIRED"
] as const;
export const VibeCardBindingStatusSchema = z.enum(VIBE_CARD_BINDING_STATUSES);

/** `VibeCardBindingStatusSchema` as a reader parses it: a value this build does not list reads as unlisted. */
export const VibeCardBindingStatusReadSchema = enumReadSchema(VIBE_CARD_BINDING_STATUSES);
export type VibeCardBindingStatusValue = z.infer<typeof VibeCardBindingStatusSchema>;
